// src/components/layouts/app/sub/NotificationPanelMobile.tsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  Tag,
  Settings,
  Users,
  Shield,
  Bell,
  BellOff,
  CheckCircle,
  X,
} from "lucide-react";

interface NotificationPanelMobileProps {
  isNotificationsVisible: boolean;
  setIsNotificationsVisible: (value: boolean) => void;
}

interface Notification {
  id: string;
  type: "order" | "promotion" | "system" | "social" | "security";
  title: string;
  message: string;
  time: string;
  read: boolean;
}

const NotificationPanelMobile: React.FC<NotificationPanelMobileProps> = ({
  isNotificationsVisible,
  setIsNotificationsVisible,
}) => {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: "1",
      type: "order",
      title: "Commande expédiée",
      message: "Votre commande #KX-2456 a été expédiée",
      time: "5 min",
      read: false,
    },
    {
      id: "2",
      type: "promotion",
      title: "Promotion flash",
      message: "-40% sur l'électronique • 24h",
      time: "30 min",
      read: false,
    },
    {
      id: "3",
      type: "system",
      title: "Mise à jour profil",
      message: "Complétez votre profil pour débloquer des avantages",
      time: "2h",
      read: true,
    },
  ]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) markAsRead(notification.id);
    // Navigation dépendante du type (exemple)
    if (notification.type === "order") navigate("/orders");
    else if (notification.type === "promotion") navigate("/deals");
    else if (notification.type === "system") navigate("/account");
    else navigate("/notifications");
    setIsNotificationsVisible(false);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      setIsNotificationsVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getTypeIcon = (type: Notification["type"]) => {
    switch (type) {
      case "order":
        return <Package className="w-5 h-5" />;
      case "promotion":
        return <Tag className="w-5 h-5" />;
      case "system":
        return <Settings className="w-5 h-5" />;
      case "social":
        return <Users className="w-5 h-5" />;
      case "security":
        return <Shield className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const getTypeBgColor = (type: Notification["type"]) => {
    switch (type) {
      case "order":
        return "bg-blue-100 text-blue-600";
      case "promotion":
        return "bg-green-100 text-green-600";
      case "system":
        return "bg-purple-100 text-purple-600";
      case "social":
        return "bg-pink-100 text-pink-600";
      case "security":
        return "bg-orange-100 text-orange-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  };

  return (
    <AnimatePresence>
      {isNotificationsVisible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm"
            onClick={() => setIsNotificationsVisible(false)}
          />

          <motion.div
            ref={panelRef}
            className="fixed inset-x-0 bottom-0 top-0 z-[60] bg-white flex flex-col  shadow-xl overflow-hidden"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-5 pt-5 pb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-white" />
                  <h2 className="text-lg font-semibold text-white">
                    Notifications
                  </h2>
                  {unreadCount > 0 && (
                    <span className="ml-2 bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setIsNotificationsVisible(false)}
                  className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-white/90 hover:text-white flex items-center gap-1.5 transition-colors"
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  Tout marquer comme lu
                </button>
              )}
            </div>

            {/* Liste des notifications */}
            <div className="flex-1 overflow-y-auto bg-gray-50">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <BellOff className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium">Aucune notification</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Les nouvelles notifications apparaîtront ici
                  </p>
                </div>
              ) : (
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="divide-y divide-gray-100"
                >
                  {notifications.map((notification) => (
                    <motion.div
                      key={notification.id}
                      variants={itemVariants}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleNotificationClick(notification)}
                      className={`
                        px-5 py-4 cursor-pointer transition-all duration-200
                        ${!notification.read ? "bg-white" : "bg-gray-50"}
                        hover:bg-gray-100 active:bg-gray-200
                      `}
                    >
                      <div className="flex gap-3">
                        <div
                          className={`
                            flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                            ${getTypeBgColor(notification.type)}
                          `}
                        >
                          {getTypeIcon(notification.type)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3
                              className={`
                                text-sm font-semibold truncate
                                ${!notification.read ? "text-gray-900" : "text-gray-600"}
                              `}
                            >
                              {notification.title}
                            </h3>
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              {notification.time}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <button
                onClick={() => {
                  navigate("/settings/notifications");
                  setIsNotificationsVisible(false);
                }}
                className="w-full py-2.5 text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Gérer les notifications
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotificationPanelMobile;